import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  auctionService,
  type AuctionBidItem,
  type AuctionItem,
  type AuctionListItem,
  type AuctionLotItem,
  type AuctionParticipantItem,
  type AuctionStatus,
  type AuctionType,
  type CreateAuctionRequest,
  type CreateAuctionLotRequest,
  type LotBiddingState,
  type ParticipantStatus,
} from '../../services/auction.service';
import './AdminAuction.css';

type FilterStatus = 'all' | AuctionStatus;

const STATUS_LABELS: Record<AuctionStatus, string> = {
  Upcoming: 'Предстоящий',
  Active: 'Идёт',
  Completed: 'Завершён',
};

const TYPE_LABELS: Record<AuctionType, string> = {
  Offline: 'Офлайн',
  Online: 'Онлайн',
  Hybrid: 'Гибридный',
};

const BIDDING_LABELS: Record<LotBiddingState, string> = {
  NotScheduled: 'Не назначены',
  NotStarted: 'Не начались',
  Open: 'Идут',
  Closed: 'Завершены',
};

const PARTICIPANT_LABELS: Record<ParticipantStatus, string> = {
  Pending: 'На рассмотрении',
  Approved: 'Допущен',
  Rejected: 'Отклонён',
  Blocked: 'Заблокирован',
};

const emptyAuctionForm: CreateAuctionRequest = {
  titleKz: '',
  titleRu: '',
  descriptionKz: '',
  descriptionRu: '',
  farmNameKz: '',
  farmNameRu: '',
  regionKz: '',
  regionRu: '',
  date: '',
  auctionType: 'Offline',
  breed: '',
  status: 'Upcoming',
  youtubeUrl: '',
  youtubeEmbedUrl: '',
  coverImage: '',
  bannerImage: '',
  minBidIncrement: 0,
};

/** Расписание торгов в форме: локальное время из input[type=datetime-local], на сервер уходит UTC. */
interface ScheduleFormState {
  startsAt: string;
  endsAt: string;
  minBidIncrement: string;
}

const emptySchedule: ScheduleFormState = { startsAt: '', endsAt: '', minBidIncrement: '' };

/** Форма лота: числовые поля храним строками, конвертируем при отправке. */
interface LotFormState {
  lotNumber: string;
  name: string;
  inj: string;
  birthDate: string;
  ageMonths: string;
  birthWeight: string;
  weaningWeight: string;
  yearlingWeight: string;
  currentWeight: string;
  averageDailyGain: string;
  scrotalCircumference: string;
  spermQualityScore: string;
  heightAtWithers: string;
  line: string;
  startingPrice: string;
  imageUrl: string;
  closesAt: string;
}

const emptyLotForm: LotFormState = {
  lotNumber: '',
  name: '',
  inj: '',
  birthDate: '',
  ageMonths: '',
  birthWeight: '',
  weaningWeight: '',
  yearlingWeight: '',
  currentWeight: '',
  averageDailyGain: '',
  scrotalCircumference: '',
  spermQualityScore: '',
  heightAtWithers: '',
  line: '',
  startingPrice: '',
  imageUrl: '',
  closesAt: '',
};

const toNumberOrNull = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

const formatPrice = (v: number) => v.toLocaleString('ru-RU');

const pad2 = (n: number) => String(n).padStart(2, '0');

/** UTC ISO → значение для input[type=datetime-local] в часовом поясе браузера. */
const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Значение input[type=datetime-local] (локальное время) → UTC ISO. */
const fromLocalInput = (v: string): string | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Текст ошибки сервера (ApiResponse.message) — в нём причина ограничения. */
const getApiError = (err: unknown, fallback: string): string => {
  if (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) {
    return err.response.data.message;
  }
  return fallback;
};

export default function AdminAuction() {
  // ── Список ──
  const [auctions, setAuctions] = useState<AuctionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');

  // ── Форма аукциона ──
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateAuctionRequest>({ ...emptyAuctionForm });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [schedule, setSchedule] = useState<ScheduleFormState>({ ...emptySchedule });

  // ── Детали аукциона (лоты) ──
  const [selected, setSelected] = useState<AuctionItem | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showLotForm, setShowLotForm] = useState(false);
  const [editingLotId, setEditingLotId] = useState<number | null>(null);
  const [lotForm, setLotForm] = useState<LotFormState>({ ...emptyLotForm });
  const [lotSubmitLoading, setLotSubmitLoading] = useState(false);
  const [lotMessage, setLotMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // ── Участники и ставки выбранного аукциона ──
  const [participants, setParticipants] = useState<AuctionParticipantItem[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [participantEmail, setParticipantEmail] = useState('');
  const [participantBusyId, setParticipantBusyId] = useState<number | null>(null);
  const [bidsLot, setBidsLot] = useState<AuctionLotItem | null>(null);
  const [bids, setBids] = useState<AuctionBidItem[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    auctionService
      .getAll(filter === 'all' ? undefined : filter)
      .then((res) => {
        if (res.success && res.data) setAuctions(res.data);
        else setError(res.message || 'Ошибка загрузки');
      })
      .catch(() => setError('Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ── Аукцион: создание / редактирование ──

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyAuctionForm });
    setSchedule({ ...emptySchedule });
    setMessage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (a: AuctionListItem | AuctionItem) => {
    // В списке нет описаний и ссылок — берём полную версию с сервера
    auctionService
      .getById(a.id)
      .then((res) => {
        if (!res.success || !res.data) return;
        const full = res.data;
        setEditingId(full.id);
        setForm({
          titleKz: full.titleKz,
          titleRu: full.titleRu,
          descriptionKz: full.descriptionKz ?? '',
          descriptionRu: full.descriptionRu ?? '',
          farmNameKz: full.farmNameKz,
          farmNameRu: full.farmNameRu,
          regionKz: full.regionKz,
          regionRu: full.regionRu,
          date: full.date,
          auctionType: full.auctionType,
          breed: full.breed,
          status: full.status,
          youtubeUrl: full.youtubeUrl ?? '',
          youtubeEmbedUrl: full.youtubeEmbedUrl ?? '',
          coverImage: full.coverImage ?? '',
          bannerImage: full.bannerImage ?? '',
          minBidIncrement: full.minBidIncrement ?? 0,
        });
        setSchedule({
          startsAt: toLocalInput(full.startsAt),
          endsAt: toLocalInput(full.endsAt),
          minBidIncrement: full.minBidIncrement ? String(full.minBidIncrement) : '',
        });
        setMessage(null);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(() => {});
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyAuctionForm });
    setSchedule({ ...emptySchedule });
    setMessage(null);
  };

  const handleChange = (field: keyof CreateAuctionRequest, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setMessage(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.titleRu.trim() || !form.titleKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите название на русском и казахском' });
      return;
    }
    if (!form.farmNameRu.trim() || !form.farmNameKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите хозяйство на русском и казахском' });
      return;
    }
    if (!form.regionRu.trim() || !form.regionKz.trim()) {
      setMessage({ type: 'err', text: 'Укажите регион на русском и казахском' });
      return;
    }
    if (!form.date) {
      setMessage({ type: 'err', text: 'Укажите дату проведения' });
      return;
    }
    if (!form.breed.trim()) {
      setMessage({ type: 'err', text: 'Укажите породу' });
      return;
    }
    const startsAt = fromLocalInput(schedule.startsAt);
    const endsAt = fromLocalInput(schedule.endsAt);
    if (!!startsAt !== !!endsAt) {
      setMessage({ type: 'err', text: 'Укажите и начало, и окончание торгов (или оставьте оба поля пустыми)' });
      return;
    }
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
      setMessage({ type: 'err', text: 'Окончание торгов должно быть позже начала' });
      return;
    }
    const minBidIncrement = toNumberOrNull(schedule.minBidIncrement) ?? 0;
    if (minBidIncrement < 0) {
      setMessage({ type: 'err', text: 'Шаг ставки не может быть отрицательным' });
      return;
    }

    const body: CreateAuctionRequest = {
      ...form,
      descriptionKz: form.descriptionKz?.trim() || null,
      descriptionRu: form.descriptionRu?.trim() || null,
      youtubeUrl: form.youtubeUrl?.trim() || null,
      youtubeEmbedUrl: form.youtubeEmbedUrl?.trim() || null,
      coverImage: form.coverImage?.trim() || null,
      bannerImage: form.bannerImage?.trim() || null,
      startsAt,
      endsAt,
      minBidIncrement,
    };

    setSubmitLoading(true);
    setMessage(null);
    const request = editingId
      ? auctionService.update(editingId, body)
      : auctionService.create(body);
    request
      .then((res) => {
        if (res.success) {
          closeForm();
          if (selected) reloadDetails();
          else load();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка сохранения' });
        }
      })
      .catch((err) => setMessage({ type: 'err', text: getApiError(err, 'Ошибка сохранения') }))
      .finally(() => setSubmitLoading(false));
  };

  const handleDelete = (a: AuctionListItem) => {
    if (!confirm(`Удалить аукцион «${a.titleRu}» вместе со всеми лотами?`)) return;
    auctionService
      .delete(a.id)
      .then((res) => {
        if (res.success) load();
        else alert(res.message || 'Ошибка удаления');
      })
      .catch((err) => alert(getApiError(err, 'Ошибка удаления')));
  };

  // ── Детали / лоты ──

  const openDetails = (id: number) => {
    setDetailsLoading(true);
    setShowLotForm(false);
    setEditingLotId(null);
    setLotForm({ ...emptyLotForm });
    setLotMessage(null);
    auctionService
      .getById(id)
      .then((res) => {
        if (res.success && res.data) setSelected(res.data);
      })
      .catch(() => {})
      .finally(() => setDetailsLoading(false));
    loadParticipants(id);
  };

  const loadParticipants = (auctionId: number) => {
    setParticipantsLoading(true);
    setParticipantsError(null);
    auctionService
      .getParticipants(auctionId)
      .then((res) => {
        if (res.success && res.data) setParticipants(res.data);
        else setParticipantsError(res.message || 'Ошибка загрузки участников');
      })
      .catch((err) => setParticipantsError(getApiError(err, 'Ошибка загрузки участников')))
      .finally(() => setParticipantsLoading(false));
  };

  const handleParticipantStatus = (p: AuctionParticipantItem, status: ParticipantStatus) => {
    if (status === 'Blocked' && !confirm(`Заблокировать участника №${p.number} (${p.email})? Новые ставки будут запрещены, принятые ставки сохранятся.`)) {
      return;
    }
    setParticipantBusyId(p.id);
    setParticipantsError(null);
    auctionService
      .updateParticipantStatus(p.id, status)
      .then((res) => {
        if (res.success && res.data) {
          const updated = res.data;
          setParticipants((list) => list.map((x) => (x.id === updated.id ? updated : x)));
        } else {
          setParticipantsError(res.message || 'Ошибка изменения статуса');
        }
      })
      .catch((err) => setParticipantsError(getApiError(err, 'Ошибка изменения статуса')))
      .finally(() => setParticipantBusyId(null));
  };

  const handleAddParticipant = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !participantEmail.trim()) return;
    setParticipantsError(null);
    auctionService
      .addParticipant(selected.id, participantEmail.trim())
      .then((res) => {
        if (res.success && res.data) {
          const added = res.data;
          setParticipants((list) => [...list, added]);
          setParticipantEmail('');
        } else {
          setParticipantsError(res.message || 'Ошибка добавления участника');
        }
      })
      .catch((err) => setParticipantsError(getApiError(err, 'Ошибка добавления участника')));
  };

  const openBids = (lot: AuctionLotItem) => {
    setBidsLot(lot);
    setBids([]);
    setBidsLoading(true);
    auctionService
      .getLotBids(lot.id)
      .then((res) => {
        if (res.success && res.data) setBids(res.data);
      })
      .catch(() => {})
      .finally(() => setBidsLoading(false));
  };

  const closeDetails = () => {
    setSelected(null);
    setParticipants([]);
    setParticipantsError(null);
    setBidsLot(null);
    setBids([]);
    setShowLotForm(false);
    setEditingLotId(null);
    setLotMessage(null);
    load();
  };

  const reloadDetails = () => {
    if (selected) openDetails(selected.id);
  };

  const openLotCreate = () => {
    setEditingLotId(null);
    const nextNumber =
      selected && selected.lots.length > 0
        ? Math.max(...selected.lots.map((l) => l.lotNumber)) + 1
        : 1;
    setLotForm({ ...emptyLotForm, lotNumber: String(nextNumber) });
    setLotMessage(null);
    setShowLotForm(true);
  };

  const openLotEdit = (lot: AuctionLotItem) => {
    setEditingLotId(lot.id);
    setLotForm({
      lotNumber: String(lot.lotNumber),
      name: lot.name,
      inj: lot.inj,
      birthDate: lot.birthDate ?? '',
      ageMonths: lot.ageMonths != null ? String(lot.ageMonths) : '',
      birthWeight: lot.birthWeight != null ? String(lot.birthWeight) : '',
      weaningWeight: lot.weaningWeight != null ? String(lot.weaningWeight) : '',
      yearlingWeight: lot.yearlingWeight != null ? String(lot.yearlingWeight) : '',
      currentWeight: lot.currentWeight != null ? String(lot.currentWeight) : '',
      averageDailyGain: lot.averageDailyGain != null ? String(lot.averageDailyGain) : '',
      scrotalCircumference:
        lot.scrotalCircumference != null ? String(lot.scrotalCircumference) : '',
      spermQualityScore: lot.spermQualityScore != null ? String(lot.spermQualityScore) : '',
      heightAtWithers: lot.heightAtWithers != null ? String(lot.heightAtWithers) : '',
      line: lot.line ?? '',
      startingPrice: String(lot.startingPrice),
      imageUrl: lot.imageUrl ?? '',
      closesAt: toLocalInput(lot.closesAt),
    });
    setLotMessage(null);
    setShowLotForm(true);
  };

  const closeLotForm = () => {
    setShowLotForm(false);
    setEditingLotId(null);
    setLotForm({ ...emptyLotForm });
    setLotMessage(null);
  };

  const handleLotChange = (field: keyof LotFormState, value: string) => {
    setLotForm((f) => ({ ...f, [field]: value }));
    setLotMessage(null);
  };

  const handleLotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const lotNumber = toNumberOrNull(lotForm.lotNumber);
    if (!lotNumber || lotNumber < 1) {
      setLotMessage({ type: 'err', text: 'Укажите корректный номер лота' });
      return;
    }
    if (!lotForm.name.trim()) {
      setLotMessage({ type: 'err', text: 'Укажите кличку животного' });
      return;
    }
    if (!lotForm.inj.trim()) {
      setLotMessage({ type: 'err', text: 'Укажите ИНЖ' });
      return;
    }
    const startingPrice = toNumberOrNull(lotForm.startingPrice);
    if (startingPrice == null || startingPrice < 0) {
      setLotMessage({ type: 'err', text: 'Укажите корректную стартовую цену' });
      return;
    }

    const body: CreateAuctionLotRequest = {
      lotNumber,
      name: lotForm.name.trim(),
      inj: lotForm.inj.trim(),
      birthDate: lotForm.birthDate || null,
      ageMonths: toNumberOrNull(lotForm.ageMonths),
      birthWeight: toNumberOrNull(lotForm.birthWeight),
      weaningWeight: toNumberOrNull(lotForm.weaningWeight),
      yearlingWeight: toNumberOrNull(lotForm.yearlingWeight),
      currentWeight: toNumberOrNull(lotForm.currentWeight),
      averageDailyGain: toNumberOrNull(lotForm.averageDailyGain),
      scrotalCircumference: toNumberOrNull(lotForm.scrotalCircumference),
      spermQualityScore: toNumberOrNull(lotForm.spermQualityScore),
      heightAtWithers: toNumberOrNull(lotForm.heightAtWithers),
      line: lotForm.line.trim() || null,
      startingPrice,
      imageUrl: lotForm.imageUrl.trim() || null,
      closesAt: fromLocalInput(lotForm.closesAt),
    };

    setLotSubmitLoading(true);
    setLotMessage(null);
    const request = editingLotId
      ? auctionService.updateLot(editingLotId, body)
      : auctionService.createLot(selected.id, body);
    request
      .then((res) => {
        if (res.success) {
          closeLotForm();
          reloadDetails();
        } else {
          setLotMessage({ type: 'err', text: res.message || 'Ошибка сохранения лота' });
        }
      })
      .catch((err) => setLotMessage({ type: 'err', text: getApiError(err, 'Ошибка сохранения лота') }))
      .finally(() => setLotSubmitLoading(false));
  };

  const handleLotDelete = (lot: AuctionLotItem) => {
    if (!confirm(`Удалить лот №${lot.lotNumber} «${lot.name}»?`)) return;
    auctionService
      .deleteLot(lot.id)
      .then((res) => {
        if (res.success) reloadDetails();
        else alert(res.message || 'Ошибка удаления лота');
      })
      .catch((err) => alert(getApiError(err, 'Ошибка удаления лота')));
  };

  // ── Рендер: детали аукциона ──

  if (selected || detailsLoading) {
    return (
      <div className="admin-auction">
        <button className="admin-auction-back-btn" onClick={closeDetails}>
          ← К списку аукционов
        </button>

        {detailsLoading || !selected ? (
          <p className="admin-auction-loading">Загрузка...</p>
        ) : (
          <>
            <div className="admin-auction-header">
              <div>
                <h1>{selected.titleRu}</h1>
                <p className="admin-auction-desc">
                  {selected.farmNameRu} · {selected.regionRu} · {formatDate(selected.date)} ·{' '}
                  {TYPE_LABELS[selected.auctionType]} · {selected.breed}
                  {'  '}
                  <span
                    className={`admin-auction-badge admin-auction-badge--${selected.status.toLowerCase()}`}
                  >
                    {STATUS_LABELS[selected.status]}
                  </span>
                </p>
                <p className="admin-auction-desc">
                  Торги: {formatDateTime(selected.startsAt)} — {formatDateTime(selected.endsAt)}
                  {selected.minBidIncrement > 0 && ` · шаг ${formatPrice(selected.minBidIncrement)} ₸`}
                  {'  '}
                  <span
                    className={`admin-auction-badge admin-auction-bidding--${selected.biddingState.toLowerCase()}`}
                  >
                    {BIDDING_LABELS[selected.biddingState]}
                  </span>
                </p>
              </div>
              <div className="admin-auction-header-actions">
                <button className="admin-auction-edit-btn" onClick={() => openEdit(selected)}>
                  Изменить аукцион
                </button>
                <button className="admin-auction-add-btn" onClick={openLotCreate}>
                  Добавить лот
                </button>
              </div>
            </div>

            {showForm && renderAuctionForm()}

            {showLotForm && (
              <form className="admin-auction-form" onSubmit={handleLotSubmit}>
                <h2 className="admin-auction-form-title">
                  {editingLotId ? 'Редактирование лота' : 'Новый лот'}
                </h2>
                <div className="admin-auction-form-grid admin-auction-form-grid--3">
                  <div className="admin-auction-row">
                    <label>Номер лота *</label>
                    <input
                      type="number"
                      min={1}
                      value={lotForm.lotNumber}
                      onChange={(e) => handleLotChange('lotNumber', e.target.value)}
                      required
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Кличка *</label>
                    <input
                      type="text"
                      value={lotForm.name}
                      onChange={(e) => handleLotChange('name', e.target.value)}
                      required
                      maxLength={200}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>ИНЖ *</label>
                    <input
                      type="text"
                      value={lotForm.inj}
                      onChange={(e) => handleLotChange('inj', e.target.value)}
                      required
                      maxLength={100}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Дата рождения</label>
                    <input
                      type="date"
                      value={lotForm.birthDate}
                      onChange={(e) => handleLotChange('birthDate', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Возраст, мес.</label>
                    <input
                      type="number"
                      min={0}
                      value={lotForm.ageMonths}
                      onChange={(e) => handleLotChange('ageMonths', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Линия</label>
                    <input
                      type="text"
                      value={lotForm.line}
                      onChange={(e) => handleLotChange('line', e.target.value)}
                      maxLength={200}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес при рождении, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.birthWeight}
                      onChange={(e) => handleLotChange('birthWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес при отъёме, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.weaningWeight}
                      onChange={(e) => handleLotChange('weaningWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Вес в 12 мес., кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.yearlingWeight}
                      onChange={(e) => handleLotChange('yearlingWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Текущий вес, кг</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.currentWeight}
                      onChange={(e) => handleLotChange('currentWeight', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Среднесуточный привес, г</label>
                    <input
                      type="number"
                      min={0}
                      value={lotForm.averageDailyGain}
                      onChange={(e) => handleLotChange('averageDailyGain', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Обхват мошонки, см</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.scrotalCircumference}
                      onChange={(e) => handleLotChange('scrotalCircumference', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Оценка спермы (1-10)</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={lotForm.spermQualityScore}
                      onChange={(e) => handleLotChange('spermQualityScore', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Высота в холке, см</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.heightAtWithers}
                      onChange={(e) => handleLotChange('heightAtWithers', e.target.value)}
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Стартовая цена, ₸ *</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={lotForm.startingPrice}
                      onChange={(e) => handleLotChange('startingPrice', e.target.value)}
                      required
                    />
                  </div>
                  <div className="admin-auction-row">
                    <label>Закрытие лота</label>
                    <input
                      type="datetime-local"
                      value={lotForm.closesAt}
                      onChange={(e) => handleLotChange('closesAt', e.target.value)}
                      disabled={!selected.startsAt}
                    />
                  </div>
                  <div className="admin-auction-row admin-auction-row--full">
                    <p className="admin-auction-hint">
                      {selected.startsAt
                        ? `Закрытие лота — позже начала (${formatDateTime(selected.startsAt)}) и не позже окончания торгов (${formatDateTime(selected.endsAt)}). Пусто — лот закрывается вместе с аукционом.`
                        : 'Чтобы задать закрытие лота, сначала укажите начало и окончание торгов в настройках аукциона.'}
                      {(() => {
                        const editingLot = selected.lots.find((l) => l.id === editingLotId);
                        return editingLot && editingLot.bidsCount > 0
                          ? ` По лоту уже принято ставок: ${editingLot.bidsCount}. Стартовую цену нельзя поднять выше первой ставки, а закрытие — назначить раньше последней ставки.`
                          : '';
                      })()}
                    </p>
                  </div>
                  <div className="admin-auction-row admin-auction-row--full">
                    <label>Фото (URL)</label>
                    <input
                      type="url"
                      value={lotForm.imageUrl}
                      onChange={(e) => handleLotChange('imageUrl', e.target.value)}
                      maxLength={2000}
                    />
                  </div>
                </div>
                {lotMessage && (
                  <p
                    className={
                      lotMessage.type === 'ok' ? 'admin-auction-msg-ok' : 'admin-auction-msg-err'
                    }
                  >
                    {lotMessage.text}
                  </p>
                )}
                <div className="admin-auction-actions">
                  <button type="submit" disabled={lotSubmitLoading}>
                    {lotSubmitLoading
                      ? 'Сохранение...'
                      : editingLotId
                        ? 'Сохранить лот'
                        : 'Создать лот'}
                  </button>
                  <button type="button" className="admin-auction-cancel-btn" onClick={closeLotForm}>
                    Отмена
                  </button>
                </div>
              </form>
            )}

            <div className="admin-auction-table-wrap">
              <table className="admin-auction-table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Лот</th>
                    <th>Характеристики</th>
                    <th>Цена, ₸</th>
                    <th>Торги</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lots.map((lot) => (
                    <tr key={lot.id}>
                      <td data-label="№">{lot.lotNumber}</td>
                      <td data-label="Лот">
                        <div className="admin-auction-cell-main">{lot.name}</div>
                        <div className="admin-auction-cell-sub">ИНЖ {lot.inj}</div>
                      </td>
                      <td data-label="Характеристики">
                        <div className="admin-auction-cell-sub">
                          {lot.birthDate ? formatDate(lot.birthDate) : '—'}
                          {lot.ageMonths != null && ` · ${lot.ageMonths} мес.`}
                        </div>
                        <div className="admin-auction-cell-sub">
                          {lot.currentWeight != null ? `${lot.currentWeight} кг` : 'вес —'}
                          {lot.line && ` · ${lot.line}`}
                        </div>
                      </td>
                      <td data-label="Цена, ₸">
                        <div className="admin-auction-cell-sub">старт {formatPrice(lot.startingPrice)}</div>
                        <div className="admin-auction-cell-main">
                          {lot.currentPrice != null ? formatPrice(lot.currentPrice) : '—'}
                        </div>
                      </td>
                      <td data-label="Торги">
                        <div className="admin-auction-cell-main">
                          {BIDDING_LABELS[lot.biddingState]}
                          {lot.result === 'Sold' && ` · продан №${lot.winnerNumber ?? '?'}`}
                          {lot.result === 'NoBids' && ' · без ставок'}
                        </div>
                        <div className="admin-auction-cell-sub">
                          до {formatDateTime(lot.biddingClosesAt)} · ставок {lot.bidsCount}
                        </div>
                      </td>
                      <td className="admin-auction-cell-actions">
                        <div className="admin-auction-row-actions">
                          <button className="admin-auction-edit-btn" onClick={() => openBids(lot)}>
                            Ставки
                          </button>
                          <button
                            className="admin-auction-edit-btn"
                            onClick={() => openLotEdit(lot)}
                          >
                            Изменить
                          </button>
                          <button
                            className="admin-auction-del-btn"
                            onClick={() => handleLotDelete(lot)}
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {selected.lots.length === 0 && (
                <p className="admin-auction-empty">В аукционе пока нет лотов</p>
              )}
            </div>

            {bidsLot && (
              <div className="admin-auction-section">
                <div className="admin-auction-section-header">
                  <h2 className="admin-auction-form-title">
                    Ставки по лоту №{bidsLot.lotNumber} «{bidsLot.name}»
                  </h2>
                  <button className="admin-auction-edit-btn" onClick={() => setBidsLot(null)}>
                    Скрыть
                  </button>
                </div>
                {bidsLoading ? (
                  <p className="admin-auction-loading">Загрузка...</p>
                ) : bids.length === 0 ? (
                  <p className="admin-auction-empty">Ставок пока нет</p>
                ) : (
                  <div className="admin-auction-table-wrap">
                    <table className="admin-auction-table">
                      <thead>
                        <tr>
                          <th>Время</th>
                          <th>Сумма, ₸</th>
                          <th>Участник</th>
                          <th>Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bids.map((b, idx) => (
                          <tr key={b.id}>
                            <td data-label="Время">{formatDateTime(b.createdAt)}</td>
                            <td data-label="Сумма, ₸">
                              {formatPrice(b.amount)}
                              {idx === 0 && ' (лидирует)'}
                            </td>
                            <td data-label="Участник">
                              №{b.bidderNumber} · {b.bidderName}
                            </td>
                            <td data-label="Email" className="admin-auction-cell-break">
                              {b.bidderEmail ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="admin-auction-section">
              <div className="admin-auction-section-header">
                <h2 className="admin-auction-form-title">Участники торгов</h2>
                <form className="admin-auction-inline-form" onSubmit={handleAddParticipant}>
                  <input
                    type="email"
                    placeholder="Email пользователя"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    maxLength={255}
                  />
                  <button type="submit" disabled={!participantEmail.trim()}>
                    Допустить
                  </button>
                </form>
              </div>
              {participantsError && <p className="admin-auction-msg-err">{participantsError}</p>}
              {participantsLoading ? (
                <p className="admin-auction-loading">Загрузка...</p>
              ) : participants.length === 0 ? (
                <p className="admin-auction-empty">Заявок на участие пока нет</p>
              ) : (
                <div className="admin-auction-table-wrap">
                  <table className="admin-auction-table">
                    <thead>
                      <tr>
                        <th>№</th>
                        <th>Участник</th>
                        <th>Заявка</th>
                        <th>Ставок</th>
                        <th>Статус</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((p) => (
                        <tr key={p.id}>
                          <td data-label="№">{p.number}</td>
                          <td data-label="Участник" className="admin-auction-cell-break">
                            <div className="admin-auction-cell-main">
                              {p.firstName} {p.lastName}
                            </div>
                            <div className="admin-auction-cell-sub">{p.email}</div>
                          </td>
                          <td data-label="Заявка">{formatDateTime(p.createdAt)}</td>
                          <td data-label="Ставок">{p.bidsCount}</td>
                          <td data-label="Статус">
                            <span
                              className={`admin-auction-badge admin-auction-participant--${p.status.toLowerCase()}`}
                            >
                              {PARTICIPANT_LABELS[p.status]}
                            </span>
                          </td>
                          <td className="admin-auction-cell-actions">
                            <div className="admin-auction-row-actions">
                              {p.status !== 'Approved' && p.status !== 'Blocked' && (
                                <button
                                  className="admin-auction-edit-btn"
                                  disabled={participantBusyId === p.id}
                                  onClick={() => handleParticipantStatus(p, 'Approved')}
                                >
                                  Допустить
                                </button>
                              )}
                              {p.status === 'Pending' && (
                                <button
                                  className="admin-auction-del-btn"
                                  disabled={participantBusyId === p.id}
                                  onClick={() => handleParticipantStatus(p, 'Rejected')}
                                >
                                  Отклонить
                                </button>
                              )}
                              {p.status !== 'Blocked' ? (
                                <button
                                  className="admin-auction-del-btn"
                                  disabled={participantBusyId === p.id}
                                  onClick={() => handleParticipantStatus(p, 'Blocked')}
                                >
                                  Заблокировать
                                </button>
                              ) : (
                                <button
                                  className="admin-auction-edit-btn"
                                  disabled={participantBusyId === p.id}
                                  onClick={() => handleParticipantStatus(p, 'Approved')}
                                >
                                  Разблокировать
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Рендер: форма аукциона (используется в списке и в деталях) ──

  function renderAuctionForm() {
    return (
      <form className="admin-auction-form" onSubmit={handleSubmit}>
        <h2 className="admin-auction-form-title">
          {editingId ? 'Редактирование аукциона' : 'Новый аукцион'}
        </h2>
        <div className="admin-auction-form-grid">
          <div className="admin-auction-row">
            <label>Название (рус) *</label>
            <input
              type="text"
              value={form.titleRu}
              onChange={(e) => handleChange('titleRu', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Название (каз) *</label>
            <input
              type="text"
              value={form.titleKz}
              onChange={(e) => handleChange('titleKz', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row admin-auction-row--full">
            <label>Описание (рус)</label>
            <textarea
              rows={3}
              value={form.descriptionRu ?? ''}
              onChange={(e) => handleChange('descriptionRu', e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="admin-auction-row admin-auction-row--full">
            <label>Описание (каз)</label>
            <textarea
              rows={3}
              value={form.descriptionKz ?? ''}
              onChange={(e) => handleChange('descriptionKz', e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Хозяйство (рус) *</label>
            <input
              type="text"
              value={form.farmNameRu}
              onChange={(e) => handleChange('farmNameRu', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Хозяйство (каз) *</label>
            <input
              type="text"
              value={form.farmNameKz}
              onChange={(e) => handleChange('farmNameKz', e.target.value)}
              required
              maxLength={500}
            />
          </div>
          <div className="admin-auction-row">
            <label>Регион (рус) *</label>
            <input
              type="text"
              value={form.regionRu}
              onChange={(e) => handleChange('regionRu', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Регион (каз) *</label>
            <input
              type="text"
              value={form.regionKz}
              onChange={(e) => handleChange('regionKz', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Дата проведения *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => handleChange('date', e.target.value)}
              required
            />
          </div>
          <div className="admin-auction-row">
            <label>Порода *</label>
            <input
              type="text"
              value={form.breed}
              onChange={(e) => handleChange('breed', e.target.value)}
              required
              maxLength={200}
            />
          </div>
          <div className="admin-auction-row">
            <label>Тип проведения *</label>
            <select
              value={form.auctionType}
              onChange={(e) => handleChange('auctionType', e.target.value)}
            >
              {(Object.keys(TYPE_LABELS) as AuctionType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-auction-row">
            <label>Статус *</label>
            <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
              {(Object.keys(STATUS_LABELS) as AuctionStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-auction-row admin-auction-row--full">
            <p className="admin-auction-hint">
              Онлайн-торги: время указывается в вашем часовом поясе. Ставки принимаются только
              между началом и окончанием — статус «Идёт» не открывает торги вне расписания,
              а статус «Завершён» закрывает их досрочно. Пустые поля — аукцион остаётся каталогом
              без ставок.
            </p>
          </div>
          <div className="admin-auction-row">
            <label>Начало торгов</label>
            <input
              type="datetime-local"
              value={schedule.startsAt}
              onChange={(e) => {
                setSchedule((sc) => ({ ...sc, startsAt: e.target.value }));
                setMessage(null);
              }}
            />
          </div>
          <div className="admin-auction-row">
            <label>Окончание торгов</label>
            <input
              type="datetime-local"
              value={schedule.endsAt}
              onChange={(e) => {
                setSchedule((sc) => ({ ...sc, endsAt: e.target.value }));
                setMessage(null);
              }}
            />
          </div>
          <div className="admin-auction-row">
            <label>Шаг ставки, ₸</label>
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="0 — любая сумма выше текущей"
              value={schedule.minBidIncrement}
              onChange={(e) => {
                setSchedule((sc) => ({ ...sc, minBidIncrement: e.target.value }));
                setMessage(null);
              }}
            />
          </div>
          <div className="admin-auction-row">
            <label>YouTube ссылка</label>
            <input
              type="url"
              value={form.youtubeUrl ?? ''}
              onChange={(e) => handleChange('youtubeUrl', e.target.value)}
              maxLength={1000}
            />
          </div>
          <div className="admin-auction-row">
            <label>YouTube embed ссылка</label>
            <input
              type="url"
              value={form.youtubeEmbedUrl ?? ''}
              onChange={(e) => handleChange('youtubeEmbedUrl', e.target.value)}
              maxLength={1000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Обложка (URL)</label>
            <input
              type="url"
              value={form.coverImage ?? ''}
              onChange={(e) => handleChange('coverImage', e.target.value)}
              maxLength={2000}
            />
          </div>
          <div className="admin-auction-row">
            <label>Баннер (URL)</label>
            <input
              type="url"
              value={form.bannerImage ?? ''}
              onChange={(e) => handleChange('bannerImage', e.target.value)}
              maxLength={2000}
            />
          </div>
        </div>
        {message && (
          <p className={message.type === 'ok' ? 'admin-auction-msg-ok' : 'admin-auction-msg-err'}>
            {message.text}
          </p>
        )}
        <div className="admin-auction-actions">
          <button type="submit" disabled={submitLoading}>
            {submitLoading
              ? 'Сохранение...'
              : editingId
                ? 'Сохранить аукцион'
                : 'Создать аукцион'}
          </button>
          <button type="button" className="admin-auction-cancel-btn" onClick={closeForm}>
            Отмена
          </button>
        </div>
      </form>
    );
  }

  // ── Рендер: список аукционов ──

  return (
    <div className="admin-auction">
      <div className="admin-auction-header">
        <div>
          <h1>Аукционы</h1>
          <p className="admin-auction-desc">Управление аукционами и лотами</p>
        </div>
        <button className="admin-auction-add-btn" onClick={openCreate}>
          Создать аукцион
        </button>
      </div>

      <div className="admin-auction-filters">
        {(['all', 'Upcoming', 'Active', 'Completed'] as FilterStatus[]).map((s) => (
          <button
            key={s}
            className={`admin-auction-filter ${filter === s ? 'admin-auction-filter--active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'Все' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {showForm && renderAuctionForm()}

      {loading ? (
        <p className="admin-auction-loading">Загрузка...</p>
      ) : error ? (
        <p className="admin-auction-error">{error}</p>
      ) : (
        <div className="admin-auction-table-wrap">
          <table className="admin-auction-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Хозяйство</th>
                <th>Регион</th>
                <th>Дата</th>
                <th>Тип</th>
                <th>Порода</th>
                <th>Статус</th>
                <th>Лотов</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {auctions.map((a) => (
                <tr key={a.id}>
                  <td data-label="ID">{a.id}</td>
                  <td data-label="Название">{a.titleRu}</td>
                  <td data-label="Хозяйство">{a.farmNameRu}</td>
                  <td data-label="Регион">{a.regionRu}</td>
                  <td data-label="Дата">{formatDate(a.date)}</td>
                  <td data-label="Тип">{TYPE_LABELS[a.auctionType]}</td>
                  <td data-label="Порода">{a.breed}</td>
                  <td data-label="Статус">
                    <span
                      className={`admin-auction-badge admin-auction-badge--${a.status.toLowerCase()}`}
                    >
                      {STATUS_LABELS[a.status]}
                    </span>
                  </td>
                  <td data-label="Лотов">{a.lotsCount}</td>
                  <td className="admin-auction-cell-actions">
                    <div className="admin-auction-row-actions">
                      <button className="admin-auction-open-btn" onClick={() => openDetails(a.id)}>
                        Открыть
                      </button>
                      <button className="admin-auction-edit-btn" onClick={() => openEdit(a)}>
                        Изменить
                      </button>
                      <button className="admin-auction-del-btn" onClick={() => handleDelete(a)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auctions.length === 0 && <p className="admin-auction-empty">Нет аукционов</p>}
        </div>
      )}
    </div>
  );
}
